export interface IDiscoveryPageState {
  previewSection: IPreviewSection;
  popUpSection: IPopUpSection;
  sdlcSection: ISDLCSection;
  discoveryPhaseSection: IDiscoveryPhaseSection;
  softwareDiscoveryPhaseSection: ISoftwareDiscoveryPhaseSection;
  softwareDiscoveryOrderSection: ISoftwareDiscoveryOrderSection;
  discoveryPhaseTypesSection: IDiscoveryPhaseTypesSection;
  discoveryPhaseBenefitsSection: IDiscoveryPhaseBenefitsSection;
  discoveryPhaseStepsSection: IDiscoveryPhaseStepsSection;
  learnMoreSection: ILearnMoreSection;
  discoveryArtifactsSection: IdiscoveryArtifactsSection;
  mainApproachesSection: IMainApproachesSection;
  discoveryProcessSection: IDiscoveryProcessSection;
  discoveryProjectsSection: IDiscoveryProjectsSection;
  discoveryEndSection: IDiscoveryEndSection;
  startBusiness: IDiscoveryStartBusiness;
}

export type IDiscoveryStartBusiness = {
  title: string;
  description: string;
};

export interface IPreviewSection {
  title: string;
  text: string[];
  imgSrc: string;
  moneySavingTitle: string;
  moneySavingText: string[];
}

export interface IPopUpSection {
  title: string;
}

export interface ISDLCSection {
  title: string;
  text: string;
  imgSrc: string;
}

export interface IDiscoveryPhaseSection {
  title: string;
  text: string[];
  imgSrc: string;
}

export interface ISoftwareDiscoveryPhaseSection {
  title: string;
  cards: IDiscoveryCards[];
}

export interface ISoftwareDiscoveryOrderSection {
  title: string;
  cards: IDiscoveryCards[];
}

export interface IDiscoveryPhaseTypesSection {
  title: string;
  cards: IDiscoveryCardsText[];
  iconSrc: string;
}

export interface IDiscoveryPhaseBenefitsSection {
  title: string;
  cards: IDiscoveryCardsText[];
}

export interface IDiscoveryPhaseStepsSection {
  imgSrc: string;
  title: string;
  text: string;
  accordion: IDiscoveryAccordion[];
}

export interface ILearnMoreSection {
  title: string;
}

export interface IdiscoveryArtifactsSection {
  title: string;
  imgSrc: string;
}

export interface IMainApproachesSection {
  title: string;
  text: string[];
  imgSrc: string;
}

export interface IDiscoveryProcessSection {
  title: string;
  cards: IDiscoveryCardsText[];
}

export interface IDiscoveryProjectsSection {
  title: string;
  cards: IDiscoveryCardsText[];
  imgSrc: string;
}

export interface IDiscoveryEndSection {
  title: string;
  cards: IDiscoveryCards[];
}

export interface IDiscoveryAccordion {
  id: number;
  title: string;
  text: IAccordionTextItem[];
}

export interface IAccordionTextItem {
  text: string;
  type: string;
}

export interface IDiscoveryCards {
  title: string;
  iconSrc: string;
  text: string;
  width: string;
  height: string;
}

export interface IDiscoveryCardsText {
  title: string;
  text: string;
}

export const discoveryPageState: IDiscoveryPageState = {
  previewSection: {
    title: `Discovery Phase Services`,
    text: [
      `According to statistics, almost half of IT projects fail without prior preparation. That's why you need the software discovery phase as part of your <div class="link-item" data-href='/blog/what-is-the-software-development-life-cycle-sdlc-and-how-does-it-work' data-content="SDLC"></div> strategy. The discovery phase helps to consider alternative solutions and technologies that will help make the business idea a reality. `,
      `The discovery stage precedes the creation of a digital platform, a website, or an application. It is aimed at identifying the main questions and objectives regarding the project, analyzing and adjusting business goals, and issuing expert recommendations on the best way to implement the client's idea. In fact, Discovery phase services are important steps toward the future success and prosperity of a startup or improvement changes to the existing product.`,
    ],
    imgSrc: `/discovery-images/introduce_image.png`,
    moneySavingTitle: `30% development costs savings: that's what you get through the Discovery Stage. `,
    moneySavingText: [
      `Moreover, you increase your chances of joining the ranks of successful startups, since almost half of IT projects fail without prior preparation. If you want to create an IT product, whether it's a B2B application for an enterprise or a B2C, it's important to start with a project scope evaluation.`,
      `Those are the reasons why your company needs the software discovery phase as part of your SDLC strategy. `,
    ],
  },
  popUpSection: {
    title: `Let us help you with a Discovery Phase`,
  },
  sdlcSection: {
    title: `What is SDLC?`,
    text: `SDLC stands for Software Development Life Cycle and describes the efficient process of digital product creation. It starts with thorough preparation, requirements gathering, analysis, and rough prototyping; then it's time for development, testing, deployment, and, of course, maintenance. Moreover, all the stages before development belong to the discovery phase steps.`,
    imgSrc: `/discovery-images/sdlc_image.png`,
  },
  discoveryPhaseSection: {
    title: `What is the Discovery Phase?`,
    text: [
      `Discovery includes all the activities that a startup needs to turn a software idea into a successful and competitive digital product, popular with users. But most importantly, Discovery allows you to approach the development process smartly and without extra costs.`,
      `It is necessary to understand the needs of users as much as possible and create documentation with the requirements for the product. All the details that we will find out during the Discovery phase will help determine the scope of work, time frame, functionality, and plan for the implementation of project tasks, and will also ensure that the customer receives quality software product development services for his needs.`,
      `In the Discovery phase, a detailed project assessment, cost calculation, user stories, prototype of mobile and web app, as well as presentations for potential investors are possible.`,
    ],
    imgSrc: `/discovery-images/time.png`,
  },
  softwareDiscoveryPhaseSection: {
    title: `When Do You Need a Software Discovery Phase?`,
    cards: [
      {
        title: `New software idea`,
        text: `Discovery is a great way to test how promising your startup idea is and whether it really has a chance to go pop.`,
        iconSrc: `/discovery-images/icn_idea.svg`,
        width: '21',
        height: '32',
      },
      {
        title: `Limited development budget`,
        text: `Resorting to Discovery, you’ll be able to decide how to start a project at no extra cost and what features should be created first.`,
        iconSrc: '/discovery-images/icn_money.svg',
        width: '32',
        height: '32',
      },
      {
        title: `Improvement of an existing product`,
        text: `Perhaps you already have a software product and want to improve it. Then Discovery will help you choose the best strategy.`,
        iconSrc: '/discovery-images/icn_improve.svg',
        width: '32',
        height: '20',
      },
      {
        title: `Any IT project`,
        text: `Would benefit from Discovery activities: at least if you want it to be a success.`,
        iconSrc: '/discovery-images/icn_project.svg',
        width: '32',
        height: '32',
      },
    ],
  },
  softwareDiscoveryOrderSection: {
    title: `Why Order Discovery Phase for Software Development?`,
    cards: [
      {
        title: `Idea clarification`,
        text: `Determine what your product needs to be in order to meet user expectations.`,
        iconSrc: '/discovery-images/icn_idea_clarification.svg',
        width: `32`,
        height: `32`,
      },
      {
        title: `Risk-free start`,
        text: `Start your project risk-free and with an understanding of all the requirements.`,
        iconSrc: '/discovery-images/icn_risk.svg',
        width: `32`,
        height: `32`,
      },
      {
        title: `Advance planning`,
        text: `Initiate your business plans on schedule, fully grasping all necessary criteria.`,
        iconSrc: '/discovery-images/icn_planning.svg',
        width: `28`,
        height: `32`,
      },
      {
        title: `Cost optimization`,
        text: `Ensure you're not losing cash or lowering quality by poor cost-cutting.`,
        iconSrc: '/discovery-images/icn_cost.svg',
        width: `32`,
        height: `32`,
      },
    ],
  },
  discoveryPhaseTypesSection: {
    title: `Discovery Phase Types`,
    cards: [
      {
        title: `Offsite type`,
        text: `When all discovery phase steps are conducted remotely: interviews, market analysis, focus groups, and everything that may be needed to achieve the goal. Undoubtedly, offsite Discovery is the most convenient and flexible way to perform research: in the end, your location doesn't really matter in this case.`,
      },
      {
        title: `Onsite type`,
        text: `Sometimes remote activities aren't enough, and a personal visit is needed to get the necessary data. And if so, one resorts to an onsite approach, which includes real meetings and personal inspections of the client's area (if the online tour won't do the trick).`,
      },
      {
        title: `Combined option`,
        text: `In some cases, onsite and offsite approaches can (and even should) be combined.`,
      },
    ],
    iconSrc: `/discovery-images/types-image.png`,
  },
  discoveryPhaseBenefitsSection: {
    title: `Discovery Phase Benefits`,
    cards: [
      {
        title: `Idea clarification`,
        text: `The discovery phase of a software project helps you clarify and refine the initial concept and see if the game is worth the candle. Starting a project, you'll know that it has a real chance to work out.`,
      },
      {
        title: `Advance planning`,
        text: `You'll be able to plan your project and prepare a resource-mobilization strategy in advance. Moreover, you won't have to act at random, you'll be using verified data from expert research.`,
      },
      {
        title: `Meeting deadlines`,
        text: `The software development discovery phase will help you figure out the best way to meet your deadlines (if meeting them is impossible, the Discovery experts will explain to you how to make adjustments to your plans).`,
      },
      {
        title: `Budget preparation`,
        text: `You'll know how much money is required to implement your project. In addition, you'll be able to determine if you can somehow reduce the budget. And if so, you'll get an understanding of what activities you should save on so as not to risk the result.`,
      },
      {
        title: `Following trends`,
        text: `Your product will be fully in line with the latest trends of your domain. Therefore, it’ll be relevant and in demand.`,
      },
      {
        title: `Risk-free start`,
        text: `You’ll get all the information you need to start your project without risks (or at least minimize them as much as possible).`,
      },
      {
        title: `Solving the user's problem`,
        text: `You'll find out what the real pain of the user is. As a result, you’ll create a sought-after product and offer your customers an excellent solution to their problems.`,
      },
      {
        title: `More chances for success`,
        text: `Having a plan of action and understanding of the basic project requirements, you have more chances to market a successful and profitable product.`,
      },
    ],
  },
  discoveryPhaseStepsSection: {
    title: `Basic Discovery Phase Steps`,
    text: `The work begins with the collection of requirements for the product. It's good if clients have terms of reference, specifications, or at least a brief description of the idea, on the basis of which there can be made (UserStory) for the MVP, according to which the development time and cost will already be calculated.`,
    imgSrc: `/discovery-images/graphic.png`,
    accordion: [
      {
        id: 1,
        title: `Discovery planning`,
        text: [
          {
            text: `It all starts with a discussion of the upcoming Discovery project. In particular, the following issues should be paid attention to:`,
            type: 'text',
          },
          {
            text: `Who will represent the client?`,
            type: `item`,
          },
          {
            text: `What experts will conduct Discovery?`,
            type: `item`,
          },
          {
            text: `What techniques should be applied in the process?`,
            type: `item`,
          },
        ],
      },
      {
        id: 2,
        title: `Processing project requirements`,
        text: [
          {
            text: `In the second stage of the discovery phase in software development, the real work begins. It involves collecting and analyzing project requirements, which is followed by making reasoned conclusions (our advice on the steps to be taken to meet these requirements):`,
            type: 'text',
          },
          {
            text: `Business requirements. We determine the product value for the client and users (separately). Also, a Discovery team works on a client's business case in order to find the scenario it covers.`,
            type: `item`,
          },
          {
            text: `Functional requirements: that is, what your software should be capable of doing, what features it must have to achieve your set goals.`,
            type: `item`,
          },
          {
            text: `Non-functional requirements. This includes everything answering the question: "How should the system perform its functions?" It’s mostly about load requirements, the number of users, product architecture, scalability, and more.`,
            type: `item`,
          },
          {
            text: `Design requirements that should turn into a product prototype: wireframes, sketches, or even mockups (whatever your project needs).`,
            type: `item`,
          },
        ],
      },
      {
        id: 3,
        title: `Vision & Scope document`,
        text: [
          {
            text: `To present the idea to stakeholders, we will make a presentation or a design concept, but if the design is on the client’s side, then we exclude these works.`,
            type: `text`,
          },
        ],
      },
    ],
  },
  learnMoreSection: {
    title: `Click to find out why the Discovery Phase should be your project starting point`,
  },
  discoveryArtifactsSection: {
    title: `Discovery Phase & its Artifacts`,
    imgSrc: `/discovery-images/graphic_large.png`,
  },
  mainApproachesSection: {
    title: `Main Approaches to the Discovery Phase`,
    imgSrc: '/discovery-images/graphic_middle.png',
    text: [
      `Agile approach is a modern and flexible research method. The project starts with the discovery phase, but that’s not all. The further development process is divided into cycles (sprints),which are preceded by Discovery analysis. Thus, we have the most up-to-date information and can make the necessary adjustments to the project at any time.`,
      `Waterfall approach, which is gradually becoming a thing of the past, especially when it comes to digital products. The Waterfall approach implies conducting Discovery at the project start, and that’s it. However, by the time the product is released, the information obtained may be out of date (the IT market is changing too quickly).`,
    ],
  },
  discoveryProcessSection: {
    title: `Who is involved in the Discovery Process?`,
    cards: [
      {
        title: `Product/Project Manager`,
        text: `Expert(s) who helps to oversee the Discovery process and conducts communication between the client and the vendor (contractor).`,
      },
      {
        title: `Business analyst`,
        text: `One of the most important members of the Discovery team. Among other things, he turns business tasks into actual project requirements.`,
      },
      {
        title: `Solution Architect`,
        text: `Responsible for technical expertise.He works with everything related to project architecture, the choice of technological solutions, and more.`,
      },
      {
        title: `UI/UX specialist`,
        text: `Who outlines software interface, which can provide the best user experience.`,
      },
      {
        title: `Subject matter expert`,
        text: `Whose task is to help the Discovery team understand the specifics of a particular domain (market).`,
      },
      {
        title: `Implementation expert`,
        text: `Professional developer with specialized experience. This expert has already dealt with similar projects.`,
      },
    ],
  },
  discoveryProjectsSection: {
    title: `Pricing Types for Discovery Projects`,
    imgSrc: `/discovery-images/costs.png`,
    cards: [
      {
        title: `Fixed Price`,
        text: `This is the most popular pricing approach. You know in advance how much you’ll have to pay and what activities will be included in the discovery phase in software development. The fixed price is the best choice for most projects (up to 2 months long).`,
      },
      {
        title: `Time and Material`,
        text: `If your project is long-term and you want to get the most out of it, you should choose a more flexible pricing model: one that covers the cost of the time and materials spent. It's the so-called post-paid approach.`,
      },
    ],
  },
  discoveryEndSection: {
    title: `What Do You Get in the End?`,
    cards: [
      {
        iconSrc: '/discovery-images/icn_clock.svg',
        width: `32`,
        height: `32`,
        title: 'Estimated development time',
        text: `The IT project discovery phase is primarily aimed at helping you determine the timing of the software development. What's more, you get an idea of the main sprint deadlines (if you choose to go with the agile approach).`,
      },
      {
        iconSrc: '/discovery-images/icn_features.svg',
        width: `32`,
        height: `19`,
        title: 'A set of must-have features',
        text: `After analyzing the market situation, your competitors, and your user needs, we compile a list of basic software features.  Our goal is to find out what features will help you achieve your business goals in the shortest possible time.`,
      },
      {
        iconSrc: '/discovery-images/icn_prototype.svg',
        width: `32`,
        height: `32`,
        title: 'Prototype(s)',
        text: `We develop a user flow, and it forms the basis of your software prototypes, which can be both static and interactive (clickable). It all depends on your project’s needs.`,
      },
      {
        iconSrc: '/discovery-images/icn_software.svg',
        width: `32`,
        height: `32`,
        title: 'Software requirements',
        text: `Also, we draw up a document describing functional and non-functional requirements for your product. We’re discussing everything that can affect the development process in one way or another.`,
      },
      {
        iconSrc: '/discovery-images/icn_stack.svg',
        width: `32`,
        height: `27`,
        title: 'Tech stack',
        text: `A very important stage of the SDLC discovery phase is the definition of development tools, which will allow creating a digital product with the specified set of functional and non-functional attributes.`,
      },
      {
        iconSrc: '/discovery-images/icn_team.svg',
        width: `32`,
        height: `25`,
        title: 'Development team',
        text: `We give a recommendation on specialists who should participate in the creation of your software product. You'll know how many experts you have to hire to bring your idea to life.`,
      },
    ],
  },
  startBusiness: {
    title: 'Plan smarter with a clear, risk-free discovery start',
    description:
      'Interested in a risk-free start? Fill in the form, and our team will reach out to start the Discovery Phase for clear goals and a precise project strategy.',
  },
};
